import {
  HowDeterminedPanel,
  type HowDeterminedSubject,
} from "@components/provenance/HowDeterminedPanel";
import {
  ASSESSMENT_PROVENANCE_QUERY,
  DECISION_SUMMARY_PROVENANCE_QUERY,
  SYNTHESIS_PROVENANCE_QUERY,
} from "@lib/graphql/queries";
import { useState } from "react";
import { useQuery } from "urql";

interface HowDeterminedButtonProps {
  campaignCandidateId?: string;
  candidateName?: string;
  subject: HowDeterminedSubject;
  className?: string;
}

function resolveQueryConfig(subject: HowDeterminedSubject, campaignCandidateId?: string) {
  switch (subject.kind) {
    case "assessment":
      return { query: ASSESSMENT_PROVENANCE_QUERY, variables: { id: campaignCandidateId } };
    case "synthesis":
      return { query: SYNTHESIS_PROVENANCE_QUERY, variables: { id: campaignCandidateId } };
    case "decision_summary":
      return { query: DECISION_SUMMARY_PROVENANCE_QUERY, variables: { id: subject.campaignId } };
  }
}

export function HowDeterminedButton({
  campaignCandidateId,
  candidateName,
  subject,
  className,
}: HowDeterminedButtonProps) {
  const [open, setOpen] = useState(false);

  const { query, variables } = resolveQueryConfig(subject, campaignCandidateId);
  const [result] = useQuery({ query, variables });

  const count = resolveNotIncludedCount(result.data, subject);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          "inline-flex items-center gap-1 text-[11px] text-text-faint underline decoration-dotted underline-offset-2 hover:text-text-secondary"
        }
        data-testid="how-determined-button"
      >
        How was this determined?
        {count > 0 && (
          <span
            className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            data-testid="not-included-badge"
          >
            {count} newer {count === 1 ? "input" : "inputs"}
          </span>
        )}
      </button>
      <HowDeterminedPanel
        open={open}
        onClose={() => setOpen(false)}
        campaignCandidateId={campaignCandidateId}
        candidateName={candidateName}
        subject={subject}
      />
    </>
  );
}

// biome-ignore lint/suspicious/noExplicitAny: urql query result shape
function resolveNotIncludedCount(data: any, subject: HowDeterminedSubject): number {
  if (subject.kind === "decision_summary") {
    return data?.campaign?.decisionSummary?.provenance?.notIncludedCount ?? 0;
  }
  if (!data?.campaignCandidate) return 0;
  if (subject.kind === "assessment") {
    return (
      data.campaignCandidate.candidateAssessment?.currentVersion?.provenance?.notIncludedCount ?? 0
    );
  }
  const steps = data.campaignCandidate.candidateSelectionSteps ?? [];
  const step = steps.find((s: { id: string }) => s.id === subject.candidateSelectionStepId);
  return step?.interviewRoundSynthesis?.currentVersion?.provenance?.notIncludedCount ?? 0;
}
